import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, BookOpen, User, Settings, BarChart3, Sparkles, Activity, Layers } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import ThemeToggle from './ThemeToggle';

const MainNavigation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const navItems = [
    { icon: MessageSquare, label: '對話', path: '/create', show: true },
    { icon: Activity, label: '影片診斷', path: '/scorecard', show: true },
    { icon: Layers, label: '輪播工廠', path: '/carousel', show: true },
    { icon: BookOpen, label: '案例庫', path: '/cases', show: true },
    { icon: BookOpen, label: '案例管理', path: '/admin/cases', show: user?.role === 'operator' },
    { icon: BarChart3, label: '數據分析', path: '/admin/stats', show: user?.role === 'admin' },
    { icon: Settings, label: '管理後台', path: '/admin', show: user?.role === 'admin' },
  ];

  const isActive = (path) => {
    if (path === '/create') return location.pathname === '/create' || location.pathname === '/';
    if (path === '/admin') return location.pathname === '/admin';
    if (path === '/admin/cases') return location.pathname.startsWith('/admin/case');
    if (path === '/cases') return location.pathname === '/cases' || location.pathname.startsWith('/cases/');
    return location.pathname === path;
  };

  return (
    <TooltipProvider delayDuration={0}>
      {/* Desktop: Left icon rail */}
      <div
        className="hidden md:flex w-[60px] bg-obsidian-950 flex-col items-center py-5 flex-shrink-0 border-r border-obsidian-600"
        data-testid="main-nav-rail"
      >
        <div className="mb-6">
          <button
            onClick={() => navigate('/')}
            className="w-9 h-9 bg-lime-500 rounded-md flex items-center justify-center hover:bg-lime-400 transition-colors"
            style={{ boxShadow: '0 0 14px rgba(255,84,54,0.35)' }}
            data-testid="nav-logo"
          >
            <Sparkles className="w-4 h-4 text-obsidian-900" strokeWidth={2.5} />
          </button>
        </div>

        <nav className="flex-1 flex flex-col gap-1.5 w-full px-2">
          {navItems.filter((item) => item.show).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <Tooltip key={item.path}>
                <TooltipTrigger asChild>
                  <button
                    onClick={() => navigate(item.path)}
                    className={`w-full aspect-square rounded-md flex items-center justify-center transition-all duration-200 relative ${
                      active
                        ? 'bg-white/[0.06] text-lime-500'
                        : 'text-obsidian-300 hover:bg-white/[0.03] hover:text-white'
                    }`}
                    data-testid={`nav-${item.path.replace(/\//g, '-')}`}
                  >
                    {active && (
                      <span
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-lime-500 rounded-r"
                        style={{ boxShadow: '0 0 6px rgba(255,84,54,0.6)' }}
                      />
                    )}
                    <Icon className="w-[18px] h-[18px]" strokeWidth={active ? 2 : 1.5} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-obsidian-700 text-white border-obsidian-600 text-xs">
                  <p>{item.label}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </nav>

        <div className="mt-auto flex flex-col items-center gap-1.5">
          <ThemeToggle className="!h-9 !w-9" />
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => navigate('/profile')}
                className="w-9 h-9 rounded-md flex items-center justify-center text-obsidian-300 hover:bg-white/[0.03] hover:text-white transition-all duration-200"
                data-testid="nav-profile"
              >
                <User className="w-[18px] h-[18px]" strokeWidth={1.5} />
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-obsidian-700 text-white border-obsidian-600 text-xs">
              <p>{user?.email}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* Mobile: Bottom tab bar */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 surface-glass-dark border-t border-obsidian-600 z-40 pb-safe"
        data-testid="main-nav-mobile"
      >
        <nav className="flex justify-around items-center h-14 px-2">
          {navItems.filter((item) => item.show).map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center justify-center flex-1 h-full transition-colors duration-200 ${
                  active ? 'text-lime-500' : 'text-obsidian-300 hover:text-white'
                }`}
                data-testid={`mobile-nav-${item.path.replace(/\//g, '-')}`}
              >
                <Icon className={`w-5 h-5 mb-0.5 ${active ? 'stroke-[2]' : 'stroke-[1.5]'}`} />
                <span className="text-[10px] font-medium truncate max-w-[56px]">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </div>
    </TooltipProvider>
  );
};

export default MainNavigation;
